import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { Skeleton } from "@monorepo-apps/ui/src/components/skeleton";

import { VerdictLegend } from "@/presentation/components/roadmaps/verdict-legend";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { DOMAIN_COLOR, DOMAIN_LABEL } from "@/presentation/data/domains";
import {
  ROADMAP_BRANCHES,
  ROADMAP_COLOR,
  ROADMAP_COLOR_SOFT,
  ROADMAP_LABEL,
  ROADMAP_TAGLINE,
  VERDICT_COLOR,
  VERDICT_LABEL,
  verdictsForBranch,
  type RoadmapBranch,
  type Verdict,
} from "@/presentation/data/roadmaps";
import {
  useRoadmapsOverview,
  type BranchStat,
  type DomainStat,
} from "@/presentation/hooks/use-roadmaps-overview";

export const Route = createFileRoute("/_app/roadmaps/")({
  component: RoadmapsOverviewPage,
});

function RoadmapsOverviewPage() {
  const overview = useRoadmapsOverview();
  return (
    <div className="flex flex-col">
      <div className="flex justify-end px-6 pt-6 print:hidden">
        <PageExplainer pageKey="roadmaps" />
      </div>
      <PageHeader
        eyebrow="Roadmaps"
        title="B2B build strategy — triage then classify"
        summary="All Features is the triage board (MVP vs Non-MVP). The MVP board takes whatever's been marked MVP and classifies it as off-the-shelf, build-on-top, or from-scratch."
      />
      <div className="animate-page-enter grid grid-cols-1 gap-5 px-6 py-6 lg:grid-cols-2">
        {ROADMAP_BRANCHES.map((branch) => (
          <BranchPanel
            key={branch}
            branch={branch}
            stat={overview.branches.find((b) => b.branch === branch) ?? null}
            isLoading={overview.isLoading}
          />
        ))}
      </div>
    </div>
  );
}

function BranchPanel({
  branch,
  stat,
  isLoading,
}: {
  branch: RoadmapBranch;
  stat: BranchStat | null;
  isLoading: boolean;
}) {
  const accent = ROADMAP_COLOR[branch];
  const soft = ROADMAP_COLOR_SOFT[branch];
  return (
    <article
      data-slot="branch-panel"
      data-branch={branch}
      className="flex flex-col gap-4 rounded-xl border bg-white p-5 print:break-inside-avoid"
      style={{ borderColor: accent }}
    >
      <BranchPanelHeader branch={branch} stat={stat} accent={accent} soft={soft} />
      <VerdictLegend branch={branch} />
      {isLoading || !stat ? (
        <DomainListSkeleton />
      ) : (
        <DomainList branch={branch} domains={stat.domains} />
      )}
    </article>
  );
}

function BranchPanelHeader({
  branch,
  stat,
  accent,
  soft,
}: {
  branch: RoadmapBranch;
  stat: BranchStat | null;
  accent: string;
  soft: string;
}) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div>
        <p
          className="font-data inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
          style={{ background: soft, color: accent }}
        >
          {ROADMAP_LABEL[branch]}
        </p>
        <h2 className="text-foreground mt-1.5 text-base font-semibold tracking-tight">
          {ROADMAP_TAGLINE[branch]}
        </h2>
        {stat ? (
          <p className="text-muted-foreground mt-1 text-xs">
            <span className="text-foreground font-medium tabular-nums">{stat.totalVerdicted}</span>{" "}
            of {stat.totalFeatures} features verdicted
          </p>
        ) : null}
      </div>
      <Link
        to="/roadmaps/$branch"
        params={{ branch }}
        className="text-foreground inline-flex items-center gap-1 text-xs font-medium hover:underline print:hidden"
      >
        Open board
        <ArrowRight className="size-3.5" />
      </Link>
    </header>
  );
}

function DomainListSkeleton() {
  return (
    <ul
      data-slot="domain-list-skeleton"
      className="flex flex-col gap-2"
      aria-label="Loading domain verdicts"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-md border border-transparent px-2 py-2"
        >
          <span className="bg-muted inline-flex size-2.5 shrink-0 rounded-full" aria-hidden />
          <Skeleton className="h-3 w-32 shrink-0" />
          <span className="flex-1">
            <Skeleton className="h-3 w-full" />
          </span>
          <Skeleton className="h-3 w-6 shrink-0" />
        </li>
      ))}
    </ul>
  );
}

function DomainList({
  branch,
  domains,
}: {
  branch: RoadmapBranch;
  domains: readonly DomainStat[];
}) {
  return (
    <ul className="flex flex-col gap-2">
      {domains.map((d) => (
        <li key={d.domain}>
          <DomainRow branch={branch} stat={d} />
        </li>
      ))}
    </ul>
  );
}

function DomainRow({ branch, stat }: { branch: RoadmapBranch; stat: DomainStat }) {
  return (
    <Link
      to="/roadmaps/$branch"
      params={{ branch }}
      search={{ domain: stat.domain }}
      className="group flex items-center gap-3 rounded-md border border-transparent px-2 py-2 transition-colors hover:border-[oklch(0.91_0.008_240)] hover:bg-[oklch(0.96_0.005_240)] print:break-inside-avoid"
    >
      <span
        className="inline-flex size-2.5 shrink-0 rounded-full"
        style={{ background: DOMAIN_COLOR[stat.domain] }}
        aria-hidden
      />
      <span className="text-foreground/85 w-44 shrink-0 text-[12px] font-medium tracking-tight">
        {DOMAIN_LABEL[stat.domain]}
      </span>
      <span className="flex-1">
        <VerdictBar branch={branch} stat={stat} />
      </span>
      <span className="text-muted-foreground font-data shrink-0 text-[11px] tabular-nums">
        {stat.total}
      </span>
    </Link>
  );
}

function VerdictBar({ branch, stat }: { branch: RoadmapBranch; stat: DomainStat }) {
  if (stat.total === 0) {
    return <span className="text-muted-foreground text-[10px] italic">no features</span>;
  }
  const verdicts = verdictsForBranch(branch);
  return (
    <span
      data-slot="verdict-bar"
      className="flex h-3 overflow-hidden rounded-sm bg-[oklch(0.94_0.004_240)]"
    >
      {verdicts.map((v) => (
        <VerdictBarSegment
          key={v}
          verdict={v}
          count={stat.byVerdict.get(v) ?? 0}
          total={stat.total}
        />
      ))}
      {stat.unverdicted > 0 ? (
        <span
          title={`${stat.unverdicted} unverdicted`}
          className="bg-[oklch(0.94_0.004_240)]"
          style={{ flex: stat.unverdicted }}
        />
      ) : null}
    </span>
  );
}

function VerdictBarSegment({
  verdict,
  count,
  total,
}: {
  verdict: Verdict;
  count: number;
  total: number;
}) {
  if (count === 0) return null;
  const color = VERDICT_COLOR[verdict];
  const pct = Math.round((count / total) * 100);
  return (
    <span
      data-verdict={verdict}
      title={`${VERDICT_LABEL[verdict]} · ${count} (${pct}%)`}
      className="block h-full"
      style={{ flex: count, background: color }}
    />
  );
}
